import { LowerCasePipe } from '@angular/common';
import { Injectable, PipeTransform } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import ticketAPIConfig from 'src/app/modules/apps/new-ticketing-dashboard/new-ticketing-dashboard-api.config.json';
import KnowledgeAPIConfig from 'src/app/modules/apps/knowledge-base/kb-api.config.json';
import { SortDirection } from '@shared/directives/sortable.directive';
import { AuthService } from './auth.service';
import { ExportService } from './export.service';
import { PopupService } from '@shared/modules/popup/services/popup.service';
import { ApiService } from './api.service';
import aiopsConfig from '../../apps/aiops-new/aiops-new-api.config.json';
import { DataStoreService } from './dataStore.service';
import { ExceptionHandlingService } from '@shared/services/exception-handling.service';
import { ToastService } from '@shared/modules/toast/services/toast.service';

import { DateTimeService } from '@shared/services/date-time.service';

import moment from 'moment-timezone';
import { RunTimeConfigService } from '@shared/services/run-time-config.service';
import { ActivatedRoute } from '@angular/router';
import { HttpHeaders } from '@angular/common/http';

interface SearchResult {
  countries: any[];
  total: number;
  totalSortedData: any[];
}

interface State {
  page: number;
  pageSize: number;
  searchTerm: string;
  sortColumn: string;
  sortDirection: SortDirection;
}

function compare(v1: any, v2: any) {
  v1 = v1
    .toString()
    .toLowerCase()
    .trim();
  v2 = v2
    .toString()
    .toLowerCase()
    .trim();
  return v1 < v2 ? -1 : v1 > v2 ? 1 : 0;
}

function compareSla(v1: any, v2: any) {
  const currentDate: any = new Date();
  const firstDiff = v1 ? v1 - currentDate : v1;
  const secDiff = v2 ? v2 - currentDate : v2;
  return firstDiff < secDiff ? -1 : firstDiff > secDiff ? 1 : 0;
}

function sort(countries: any[], column: string, direction: string): any[] {
  if (direction === '') {
    return countries;
  } else {
    if (column !== 'sla') {
      return [...countries].sort((a, b) => {
        a[column] = a[column] === null ? '' : a[column];
        b[column] = b[column] === null ? '' : b[column];
        const res = compare(a[column], b[column]);
        return direction === 'asc' ? res : -res;
      });
    } else {
      return [...countries].sort((a, b) => {
        const aCol: any = a[column] ? new Date(a[column]) : a[column];
        const bCol: any = b[column] ? new Date(b[column]) : b[column];
        const currDate = new Date();
        const res = compareSla(aCol, bCol);
        return direction === 'asc' ? res : -res;
      });
    }
  }
}

@Injectable({ providedIn: 'root' })
export class TicketsService {
  public linkKOId: any;

  public globalTicketList: any;

  get autoTaskSelected$() {
    return this._autoTaskSelected$;
  }

  set autoTaskSelected$(value: any) {
    this._autoTaskSelected$ = value;
  }
  get loading$() {
    return this._loading$.asObservable();
  }

  get countries$() {
    return this._countries$.asObservable();
  }

  get datedurationrange$() {
    return this._dateduration$.asObservable();
  }

  get totalData$() {
    return this._totalData$.asObservable();
  }

  get total$() {
    return this._total$.asObservable();
  }

  get isTicketError$() {
    return this._isError$;
  }

  set isTicketError$(value: boolean) {
    this._isError$ = value;
  }

  get isTicketListAccessError$() {
    return this._isTicketListAccessError$;
  }

  set isTicketListAccessError$(value: boolean) {
    this._isTicketListAccessError$ = value;
  }

  get page() {
    return this._state.page;
  }

  set page(page: number) {
    this._set({ page });
  }

  get pageSize() {
    return this._state.pageSize;
  }

  set pageSize(pageSize: number) {
    this._set({ pageSize });
  }

  get searchTerm() {
    return this._state.searchTerm;
  }

  set searchTerm(searchTerm: string) {
    this._set({ searchTerm });
  }

  set sortColumn(sortColumn: string) {
    this._set({ sortColumn });
  }

  set sortDirection(sortDirection: SortDirection) {
    this._set({ sortDirection });
  }
  isApicallCompleted: boolean;
  loggedInUserSysId: any;

  statusConstants: any;

  userSysId: string;
  assignmentGroups: any;
  ticketDataType: any;

  dashboardData: any[];
  ticketingSettings: any;
  range: any;

  public allDashboardData: any;

  private _search$ = new Subject<void>();
  private _state: State = {
    page: 1,
    pageSize: 20, // to be changed to the number of records as per pagination
    searchTerm: '',
    sortColumn: '',
    sortDirection: ''
  };
  private _isError$: boolean;
  private _autoTaskSelected$ = true;
  private _isTicketListAccessError$: boolean;
  private initialData = new BehaviorSubject<any[]>([]);
  initialData$ = this.initialData.asObservable();
  private _loading$ = new BehaviorSubject<boolean>(true);

  private _countries$ = new BehaviorSubject<any[]>([]);

  private _totalData$ = new BehaviorSubject<any[]>([]);

  private _dateduration$ = new BehaviorSubject<any[]>([]);

  private _total$ = new BehaviorSubject<number>(0);

  constructor(
    private pipe: LowerCasePipe,
    private apiService: ApiService,
    private authService: AuthService,
    private exportService: ExportService,
    private popupService: PopupService,
    private toastService: ToastService,
    private dataStore: DataStoreService,
    private exceptionHandlingService: ExceptionHandlingService,
    private dateTimeService: DateTimeService,
    private runTimeConfigService: RunTimeConfigService
  ) {
    this.runTimeConfigService
      .getAppSpecificSettings('ticketing-new-settings.config.json')
      .subscribe(ticketingSettings => {
        this.ticketingSettings = ticketingSettings;
      });
    // this.headersList = this.headersList.append('tenant-name', 'CGTest1');
  }

  getIncidentCountData(fromdate: string, todate: string) {
    const assignDetails: any = {};
    const incidentEndpoint = ticketAPIConfig.getIncidentCount;
    assignDetails.userGroupList = [];
    const dateRange = {
      fromDate: fromdate,
      toDate: todate
    };
    this.getTicketsBasedOnUser(dateRange);
    this.assignmentGroups.filter(i => {
      assignDetails.userGroupList.push({
        sys_id: i.assignment_group_id
      });
    });

    return this.apiService.post(incidentEndpoint, {
      fromdate,
      todate,
      userGroupList: assignDetails.userGroupList,
      approval_status: '1'
    });
  }

  getSCTaskCountData(fromdate: string, todate: string) {
    const sctaskEndpoint = ticketAPIConfig.getServiceTaskCount;
    const assignDetails: any = {};
    assignDetails.userGroupList = [];
    const dateRange = {
      fromDate: fromdate,
      toDate: todate
    };
    this.getTicketsBasedOnUser(dateRange);
    this.assignmentGroups.filter(i => {
      assignDetails.userGroupList.push({
        sys_id: i.assignment_group_id
      });
    });
    return this.apiService.post(sctaskEndpoint, {
      fromdate,
      todate,
      userGroupList: assignDetails.userGroupList,
      approval_status: '1'
    });
  }

  getResetTimerData(fromdate: string, todate: string, states: any = []): Observable<any> {
    let endPoint: any;
    const assignDetails: any = {};
    if (!this.ticketDataType || this.ticketDataType === 'myTickets') {
      assignDetails.userList = [];
      assignDetails.states = [];

      assignDetails.userList = [
        {
          sys_id: this.loggedInUserSysId
        }
      ];
      // const custDate = this.calculateLastDate(30);
      // assignDetails.fromdate = fromdate ? fromdate : this.formatDate(custDate);
      // assignDetails.todate = todate ? todate : this.formatDate(new Date());
      assignDetails.fromdate = this.getDates(fromdate, todate).fromdate;
      assignDetails.todate = this.getDates(fromdate, todate).todate;

      states.forEach(selstates => {
        assignDetails.states.push(selstates);
      });

      endPoint = ticketAPIConfig.getIncidentKOListBasedOnUser;
    } else if (this.ticketDataType === 'assignmentGroupTickets' && this.assignmentGroups) {
      // parse to extract values
      assignDetails.userGroupList = [];
      assignDetails.states = [];

      this.assignmentGroups.filter(i => {
        assignDetails.userGroupList.push({
          sys_id: i.assignment_group_id
        });
      });
      // const custDate = this.calculateLastDate(30);
      // assignDetails.fromdate = fromdate ? fromdate : this.formatDate(custDate);
      // assignDetails.todate = todate ? todate : this.formatDate(new Date());
      states.forEach(selstates => {
        assignDetails.states.push(selstates);
      });
      assignDetails.fromdate = this.getDates(fromdate, todate).fromdate;
      assignDetails.todate = this.getDates(fromdate, todate).todate;
      endPoint = ticketAPIConfig.getIncidentKOListBasedOnGroup;
    } else if (this.ticketDataType === 'myApprovals' && this.assignmentGroups) {
      // parse to extract values
      assignDetails.userGroupList = [];

      this.assignmentGroups.filter(i => {
        assignDetails.userGroupList.push({
          sys_id: i.assignment_group_id
        });
      });
      assignDetails.fromdate = this.getDates(fromdate, todate).fromdate;
      assignDetails.todate = this.getDates(fromdate, todate).todate;
      assignDetails.approval_status = '1';
      if (this.autoTaskSelected$) {
        // const userlist: any = [];
        // assignDetails.userGroupList.forEach(item => {
        //   userlist.push(item.sys_id);
        // });
        // assignDetails.userGroupList = [];

        // assignDetails.userGroupList = userlist;
        endPoint = ticketAPIConfig.getServiceTaskForSolutionApproval;
      } else {
        endPoint = ticketAPIConfig.getIncidentForAutomationApproval;
      }
    } else if (this.ticketDataType === 'myTasks' && this.assignmentGroups) {
      // parse to extract values
      assignDetails.userList = [];
      assignDetails.states = [];

      assignDetails.userList = [this.loggedInUserSysId];

      states.forEach(selstates => {
        assignDetails.states.push(selstates);
      });

      assignDetails.fromdate = this.getDates(fromdate, todate).fromdate;
      assignDetails.todate = this.getDates(fromdate, todate).todate;

      endPoint = ticketAPIConfig.getServiceTaskListBasedOnUser;
    } else if (this.ticketDataType === 'assignmentGroupTasks' && this.assignmentGroups) {
      // parse to extract values
      assignDetails.userGroupList = [];
      assignDetails.states = [];

      states.forEach(selstates => {
        assignDetails.states.push(selstates);
      });

      this.assignmentGroups.filter(i => {
        assignDetails.userGroupList.push(i.assignment_group_id);
      });

      assignDetails.fromdate = this.getDates(fromdate, todate).fromdate;
      assignDetails.todate = this.getDates(fromdate, todate).todate;

      endPoint = ticketAPIConfig.getServiceTaskListBasedOnGroup;
    } else {
      console.error('Data inconsistency issue');
    }

    this.isTicketListAccessError$ = false;
    this.isTicketError$ = false;
    return this.apiService.post(endPoint, assignDetails).pipe(
      map((response: any) => {
        if (!response || (response && response.length === 0)) {
          this.isTicketError$ = true;
        }
        return response;
      }),
      catchError(error => {
        this.isTicketListAccessError$ = this.exceptionHandlingService.isAccessUnauthorized(error);
        return throwError(error);
      })
    ); // end of pipe
  }

  getDates(fromdate: string, todate: string) {
    // if (!fromdate && !todate) {
    const _custDate = this.dateTimeService.formatDateTimeToCIAPFormat(this.calculateLastDate(30), null, true); // this.calculateLastDate(30);
    let _fromdate;
    let _todate;

    if (fromdate) {
      _fromdate = this.dateTimeService.formatDateTimeToCIAPFormat(fromdate, null, true);
    }

    if (todate) {
      _todate = this.dateTimeService.formatDateTimeToCIAPFormat(todate, null, true);
    }

    const _currDate = this.dateTimeService.formatDateTimeToCIAPFormat(new Date(), null, true);
    return {
      fromdate: fromdate ? _fromdate : _custDate, // fromdate ? fromdate : this.formatDate(custDate);
      todate: todate ? _todate : _currDate // todate ? todate : this.formatDate(new Date());
    };
    // } else {
    //   return {
    //     fromdate,
    //     todate
    //   };
    // }
  }

  getAttachmentDetails(sysId: string): Observable<any> {
    return this.apiService.post(ticketAPIConfig.getAttachmentDetails, { id: sysId });
  }

  getDynamicStatusAsPerClient(type: any) {
    // let type: any;
    // type = this.getCanType();

    return this.apiService.get(ticketAPIConfig.getDynamicStatusAsPerClient, {}, [
      { param: 'elements', replaceWith: 'state' },
      { param: 'type', replaceWith: type }
    ]);
  }

  getIncidentScenarios(body: any) {
    return this.apiService.post(aiopsConfig.getIncidentScenarios, body);
  }

  getJBPMTooltip(nodeId: any, referenceId: any) {
    const headers = new HttpHeaders().set('JBPMCT-Authorization', 'Basic d2JhZG1pbjp3YmFkbWlu');
    const body: {} = {
      'order-by': null,
      'order-asc': false,
      'query-params': [
        {
          'cond-column': 'nodeId',
          'cond-operator': 'IN',
          'cond-values': [nodeId]
        },
        {
          'cond-column': 'processinstanceid',
          'cond-operator': 'IN',
          'cond-values': [referenceId]
        }
      ],
      'result-column-mapping': null,
      'order-by-clause': null
    };
    return this.apiService.post(ticketAPIConfig.getTooltipDataJBPM, body, { headers });
  }

  getJBPMChildHeader(referenceId: any) {
    const headers = new HttpHeaders().set('JBPMCT-Authorization', 'Basic d2JhZG1pbjp3YmFkbWlu');
    const body: {} = {
      'order-by': null,
      'order-asc': false,
      'query-params': [
        {
          'cond-column': 'processinstanceid',
          'cond-operator': 'IN',
          'cond-values': [referenceId]
        }
      ],
      'result-column-mapping': null,
      'order-by-clause': null
    };

    return this.apiService.post(ticketAPIConfig.getChildHeaderData, body, { headers });
  }

  formatDate(date: Date) {
    const d = new Date(date);
    let month = '' + (d.getMonth() + 1);
    let day = '' + d.getDate();
    const year = d.getFullYear();

    if (month.length < 2) {
      month = '0' + month;
    }
    if (day.length < 2) {
      day = '0' + day;
    }

    return [year, month, day].join('-');
  }

  calculateLastDate(days: number) {
    // const calcDate = new Date();
    // if (days === 29) {
    //   calcDate.setMonth(calcDate.getMonth() - 1);
    // } else {
    //   calcDate.setDate(calcDate.getDate() - days);
    // }

    return moment()
      .subtract(days, 'days')
      .format('YYYY-MM-DD');
  }

  compare(a: any, b: any) {
    // Use toUpperCase() to ignore character casing
    const ticketA = a.number.toUpperCase();
    const ticketB = b.number.toUpperCase();

    let comparison = 0;
    if (ticketA > ticketB) {
      comparison = -1;
    } else if (ticketA < ticketB) {
      comparison = 1;
    }
    return comparison;
  }

  compareOnRefId(a: any, b: any) {
    // Use toUpperCase() to ignore character casing
    const ticketA = +a.referenceId;
    const ticketB = +b.referenceId;

    let comparison = 0;
    if (ticketA > ticketB) {
      comparison = 1;
    } else if (ticketA < ticketB) {
      comparison = -1;
    }
    return comparison;
  }

  getDashboardData(fromDate: string, toDate: string, states: any = []) {
    let endPoint: any;
    const assignDetails: any = {};
    this.isApicallCompleted = false;
    this.isTicketListAccessError$ = false;
    this.isTicketError$ = false;

    // setTimeout(() => {
    if (!this.ticketDataType || this.ticketDataType === 'myTickets') {
      assignDetails.userList = [];
      assignDetails.states = [];
      assignDetails.userList = [
        {
          sys_id: this.loggedInUserSysId
        }
      ];
      // const custDate = this.calculateLastDate(30);
      // assignDetails.fromdate = fromDate ? fromDate : this.formatDate(custDate);
      // assignDetails.todate = toDate ? toDate : this.formatDate(new Date());
      assignDetails.fromdate = this.getDates(fromDate, toDate).fromdate;
      assignDetails.todate = this.getDates(fromDate, toDate).todate;

      states.forEach(selstates => {
        assignDetails.states.push(selstates);
      });

      endPoint = ticketAPIConfig.getIncidentKOListBasedOnUser;
    } else if (this.ticketDataType === 'assignmentGroupTickets' && this.assignmentGroups) {
      // parse to extract values
      assignDetails.userGroupList = [];
      assignDetails.states = [];

      this.assignmentGroups.filter(i => {
        assignDetails.userGroupList.push({
          sys_id: i.assignment_group_id
        });
      });
      // const custDate = this.calculateLastDate(30);
      // assignDetails.fromdate = fromDate ? fromDate : this.formatDate(custDate);
      // assignDetails.todate = toDate ? toDate : this.formatDate(new Date());
      assignDetails.fromdate = this.getDates(fromDate, toDate).fromdate;
      assignDetails.todate = this.getDates(fromDate, toDate).todate;

      states.forEach(selstates => {
        assignDetails.states.push(selstates);
      });

      endPoint = ticketAPIConfig.getIncidentKOListBasedOnGroup;
    } else if (this.ticketDataType === 'myApprovals' && this.assignmentGroups) {
      // parse to extract values
      assignDetails.userGroupList = [];
      assignDetails.states = [];

      this.assignmentGroups.filter(i => {
        assignDetails.userGroupList.push({
          sys_id: i.assignment_group_id
        });
      });

      assignDetails.fromdate = this.getDates(fromDate, toDate).fromdate;
      assignDetails.todate = this.getDates(fromDate, toDate).todate;
      assignDetails.approval_status = '1';

      states.forEach(selstates => {
        assignDetails.states.push(selstates);
      });

      if (this.autoTaskSelected$) {
        console.log(' assignDetails.states ', assignDetails.states);
        endPoint = ticketAPIConfig.getServiceTaskForSolutionApproval;
      } else {
        console.log(' inside method - incident  ');
        console.log(' assignDetails.states ', assignDetails.states);
        endPoint = ticketAPIConfig.getIncidentForAutomationApproval;
      }
    } else if (this.ticketDataType === 'myTasks' && this.assignmentGroups) {
      // parse to extract values
      assignDetails.userList = [];
      assignDetails.states = [];
      assignDetails.userList = [this.loggedInUserSysId];

      assignDetails.fromdate = this.getDates(fromDate, toDate).fromdate;
      assignDetails.todate = this.getDates(fromDate, toDate).todate;
      states.forEach(selstates => {
        assignDetails.states.push(selstates);
      });

      endPoint = ticketAPIConfig.getServiceTaskListBasedOnUser;
    } else if (this.ticketDataType === 'assignmentGroupTasks' && this.assignmentGroups) {
      // parse to extract values
      assignDetails.userGroupList = [];
      assignDetails.states = [];
      this.assignmentGroups.filter(i => {
        assignDetails.userGroupList.push(i.assignment_group_id);
      });

      assignDetails.fromdate = this.getDates(fromDate, toDate).fromdate;
      assignDetails.todate = this.getDates(fromDate, toDate).todate;

      states.forEach(selstates => {
        assignDetails.states.push(selstates);
      });

      endPoint = ticketAPIConfig.getServiceTaskListBasedOnGroup;
    } else {
      console.error('Data inconsistency issue');
    }

    if (this.ticketDataType === 'myApprovals' && assignDetails.states.length > 0) {
      const userId = this.authService.getAuthenticatedUserId();
      this.allDashboardData = [];
      // this.dataStore.updateData(true, userId, 'originalDashboardData', []);
      console.log('assignDetails --- ', assignDetails);
      this.apiService.post(endPoint, assignDetails).subscribe(
        (data: any) => {
          this.dashboardData = data;
          this.initialData.next(this.dashboardData);
          console.log('service file initialdata', this.dashboardData);
          this.allDashboardData = data;
          // try {
          //   this.dataStore.updateData(true, userId, 'originalDashboardData', data);
          // } catch (err) {
          //   this.dashboardData = [];
          //   this.toastService.showToast(
          //     'The fetched records limit exceeds the internal filter limit. Please try selecting the date range lower than the currently selected date range. ',
          //     'toastWarning'
          //   );
          // }
          // if (JSON.stringify(data).length <= 4500000) {
          //   this.dataStore.updateData(true, userId, 'originalDashboardData', JSON.stringify(data));
          //   window.name = '';
          // } else {
          //   const tempdata = window.name;
          //   window.name = JSON.stringify(data);
          // }

          this.isApicallCompleted = true;
          // this.dashboardData.length === 0 ? (this.isTicketError$ = true) : (this.isTicketError$ = false);
          if (!this.dashboardData || this.dashboardData.length === undefined || this.dashboardData.length === 0) {
            // this.isTicketError$ = true;
            this.resetTicketData();
          } else {
            this.dashboardData.forEach(dashboardRow => {
              dashboardRow.isSelected = false;
            });
            this._search$
              .pipe(
                tap(() => this._loading$.next(true)),
                switchMap(() => this._search()),
                tap(() => this._loading$.next(false))
              )
              .subscribe(result => {
                this.isTicketError$ = false;
                this._countries$.next(result.countries);
                this._total$.next(result.total);
                this._totalData$.next.bind(result.totalSortedData);
              });

            this._search$.next();
          }
        },
        error => {
          this.resetTicketData();
          // this._totalData$.error(error);
          this.isApicallCompleted = true;
          this.isTicketListAccessError$ = this.exceptionHandlingService.isAccessUnauthorized(error);
          this._search$.next();
        }
      );
    } else if (
      (this.ticketDataType === 'myTickets' ||
        this.ticketDataType === 'assignmentGroupTickets' ||
        this.ticketDataType === 'assignmentGroupTasks' ||
        this.ticketDataType === 'myTasks') &&
      assignDetails.states.length > 0
    ) {
      const userId = this.authService.getAuthenticatedUserId();
      // this.dataStore.updateData(true, userId, 'originalDashboardData', []);
      this.allDashboardData = [];
      this.apiService.post(endPoint, assignDetails).subscribe(
        (data: any) => {
          this.dashboardData = data;

          this.initialData.next(this.dashboardData);
          console.log('service file initialdata', this.dashboardData);
          this.allDashboardData = data;
          // try {
          //   this.dataStore.updateData(true, userId, 'originalDashboardData', data);
          // } catch (err) {
          //   this.dashboardData = [];
          //   this.toastService.showToast(
          //     'The fetched records limit exceeds the internal filter limit. Please try selecting the date range lower than the currently selected date range. ',
          //     'toastWarning'
          //   );
          // }
          this.isApicallCompleted = true;
          if (!this.dashboardData || this.dashboardData.length === undefined || this.dashboardData.length === 0) {
            // this.isTicketError$ = true;
            this.resetTicketData();
          } else {
            this.dashboardData.forEach(dashboardRow => {
              dashboardRow.isSelected = false;
            });
            this._search$
              .pipe(
                tap(() => this._loading$.next(true)),
                switchMap(() => this._search()),
                tap(() => this._loading$.next(false))
              )
              .subscribe(result => {
                this.isTicketError$ = false;
                this._countries$.next(result.countries);
                this._total$.next(result.total);
                this._totalData$.next.bind(result.totalSortedData);
              });

            this._search$.next();
          }
        },
        error => {
          this.resetTicketData();
          // this._totalData$.error(error);
          this.isApicallCompleted = true;
          this.isTicketListAccessError$ = this.exceptionHandlingService.isAccessUnauthorized(error);
          this._search$.next();
        }
      );
    }

    // this.apiService.post(endPoint, assignDetails).subscribe(
    //   (data: any) => {
    //     this.dashboardData = data;
    //     this.isApicallCompleted = true;
    //     // this.dashboardData.length === 0 ? (this.isTicketError$ = true) : (this.isTicketError$ = false);
    //     if (!this.dashboardData || this.dashboardData.length === undefined || this.dashboardData.length === 0) {
    //       // this.isTicketError$ = true;
    //       this.resetTicketData();
    //     } else {
    //       this.dashboardData.forEach(dashboardRow => {
    //         dashboardRow.isSelected = false;
    //       });
    //       this._search$
    //         .pipe(
    //           tap(() => this._loading$.next(true)),
    //           switchMap(() => this._search()),
    //           tap(() => this._loading$.next(false))
    //         )
    //         .subscribe(result => {
    //           this.isTicketError$ = false;
    //           this._countries$.next(result.countries);
    //           this._total$.next(result.total);
    //           this._totalData$.next(result.totalSortedData);
    //         });

    //       this._search$.next();
    //     }
    //   },
    //   error => {
    //     this.resetTicketData();
    //     // this._totalData$.error(error);
    //     this.isApicallCompleted = true;
    //     this.isTicketListAccessError$ = this.exceptionHandlingService.isAccessUnauthorized(error);
    //     this._search$.next();
    //   }
    // );
    // });
  }

  setDashboardData(ticketsDashboard: any[]) {
    this.dashboardData = ticketsDashboard;
    this.dashboardData.forEach(dashboardRow => {
      dashboardRow.isSelected = false;
    });
    if (ticketsDashboard.length === 0) {
      this.resetTicketData();
    }

    this._search$
      .pipe(
        tap(() => this._loading$.next(true)),
        switchMap(() => this._search()),
        tap(() => this._loading$.next(false))
      )
      .subscribe(
        result => {
          this._countries$.next(result.countries);
          this._total$.next(result.total);
          this._totalData$.next.bind(result.totalSortedData);
        },
        error => {
          // this._countries$.next([]);
          // this._total$.next(0);
          // this._totalData$.next([]);
        }
      );

    this._search$.next();
  }

  resetTicketData() {
    this.isTicketError$ = true;
    this.dashboardData = [];
    this._countries$.next([]);
    this._total$.next(0);
    this._totalData$.next([]);
  }

  runBot(ticketData: any) {
    const botParam = {
      sys_id: ticketData.sys_id,
      approval_status: 0,
      solutions: [],
      description: ticketData.description,
      created_by: 'CommandTower',
      queue_name: ticketData.queue_name,
      // subcategory: ticketData.subcategory,
      solution_execution_mode: 'CommandTower'
      // category: ticketData.category
    };
    // multi-solution change
    if (ticketData.solutions && ticketData.solutions.length > 0 && ticketData.solutions[0].solution_id) {
      // botParam.solutions = JSON.parse(JSON.stringify(ticketData.solutions));
      botParam.solutions = [
        {
          solution_id:
            ticketData.solutions[0].solution_id && ticketData.solutions[0].solution_id.length > 0
              ? ticketData.solutions[0].solution_id
              : null,
          allowresolve:
            ticketData.solutions[0].allowresolve && ticketData.solutions[0].allowresolve.length > 0
              ? ticketData.solutions[0].allowresolve
              : null,
          recommendation_message:
            ticketData.solutions[0].recommendation_message && ticketData.solutions[0].recommendation_message.length > 0
              ? ticketData.solutions[0].recommendation_message
              : null,
          checkresolveflag:
            ticketData.solutions[0].checkresolveflag && ticketData.solutions[0].checkresolveflag.length > 0
              ? ticketData.solutions[0].checkresolveflag
              : null,
          inputparams:
            ticketData.solutions[0].inputparams && ticketData.solutions[0].inputparams.length > 0
              ? ticketData.solutions[0].inputparams
              : null,
          solution_seq_no:
            ticketData.solutions[0].solution_seq_no && ticketData.solutions[0].solution_seq_no.length > 0
              ? ticketData.solutions[0].solution_seq_no
              : null,
          application_name:
            ticketData.solutions[0].application_name && ticketData.solutions[0].application_name.length > 0
              ? ticketData.solutions[0].application_name
              : null,
          application_url:
            ticketData.solutions[0].application_url && ticketData.solutions[0].application_url.length > 0
              ? ticketData.solutions[0].application_url
              : null,
          solution_score:
            ticketData.solutions[0].solution_score && ticketData.solutions[0].solution_score.length > 0
              ? ticketData.solutions[0].solution_score
              : null,
          proposed_timestamp:
            ticketData.solutions[0].proposed_timestamp && ticketData.solutions[0].proposed_timestamp.length > 0
              ? ticketData.solutions[0].proposed_timestamp
              : null,
          category:
            ticketData.solutions[0].category && ticketData.solutions[0].category.length > 0
              ? ticketData.solutions[0].category
              : null,
          subcategory:
            ticketData.solutions[0].subcategory && ticketData.solutions[0].subcategory.length > 0
              ? ticketData.solutions[0].subcategory
              : null,
          ner_required: ticketData.solutions[0].ner_required,
          ner_details: ticketData.solutions[0].ner_details
        }
      ];
    }

    const appConfig = this.runTimeConfigService.getConfig('appConfig');
    const envName = appConfig.environment;
    // todo: Need to update this code once api configs are moved to root level

    let endPoint = ticketAPIConfig.rerouteTicket;
    if (this.ticketDataType === 'myTasks' || this.ticketDataType === 'assignmentGroupTasks') {
      endPoint = ticketAPIConfig.rerouteServiceTask;
    }

    return this.apiService.post(endPoint, botParam);
  }

  getTicketDetailData(ticketId: string) {
    // return this.apiService.get(ticketAPIConfig.getIncidentWithAttachmentKODetails, null, [
    //   { param: 'sysid', replaceWith: ticketId }
    // ]);
    console.log();
    const userId = this.authService.getAuthenticatedUserId();
    const globalSearchTxt = this.dataStore.getData(true, userId, 'ticketingGlobalSearch');
    if (this.ticketDataType === 'myApprovals') {
      if (this.autoTaskSelected$) {
        return this.apiService.post(ticketAPIConfig.getTaskWithAttachmentKODetails, {
          id: ticketId
        });
      } else {
        return this.apiService.post(ticketAPIConfig.getIncidentWithAttachmentKODetails, {
          id: ticketId
        });
      }
    } else {
      if (this.ticketDataType !== 'myTasks' && this.ticketDataType !== 'assignmentGroupTasks') {
        return this.apiService.post(ticketAPIConfig.getIncidentWithAttachmentKODetails, {
          id: ticketId
        });
      } else {
        return this.apiService.post(ticketAPIConfig.getTaskWithAttachmentKODetails, {
          id: ticketId
        });
      }
    }
  }

  registerLike(uid: string, ticketId?: string) {
    return this.apiService.post(ticketAPIConfig.userFeedBack, {
      solutionnumber: uid,
      incidentnumber: ticketId
      // incident_type:
      //   this.ticketDataType === 'myTasks' || this.ticketDataType === 'assignmentGroupTasks' ? 'task' : 'incident'
    });
  }

  registerLikeEKS(kb_sys_id: string, kb_article: string) {
    const appConfig = this.runTimeConfigService.getConfig('appConfig');
    const envName = appConfig.environment;

    const endPoint =
      this.ticketDataType === 'myTasks' || this.ticketDataType === 'assignmentGroupTasks'
        ? ticketAPIConfig.updateServiceTask
        : ticketAPIConfig.updateTicket;

    // todo: Need to update this code once api configs are moved to root level
    return this.apiService.post(endPoint, {
      sys_id: kb_sys_id,
      kb_article,
      created_by: 'CommandTower'
    });
  }

  getAssignmentGroupForUser(user_sys_id: string) {
    return this.apiService.post(ticketAPIConfig.getGroupsBasedOnUser, {
      id: user_sys_id
    });
  }

  getBotDataList() {
    return this.apiService.get(ticketAPIConfig.getSolutionList);
  }

  sortOnFlag(a: any, b: any) {
    return a.checkresolveflag === b.checkresolveflag ? 0 : +(a.checkresolveflag > b.checkresolveflag) || -1;
  }

  getExpiryTime(slaDate: number): string {
    let expiryTime = '';
    if (slaDate) {
      // const result = slaDate.split(' ');
      // const date = result[0].trim().split('-');
      // const time = result[1].trim().split(':');
      // const endDate = new Date();
      // const purchaseDate = new Date(
      //   parseInt(date[0], 10),
      //   parseInt(date[1], 10) - 1,
      //   parseInt(date[2], 10),
      //   parseInt(time[0], 10),
      //   parseInt(time[1], 10),
      //   parseInt(time[2], 10)
      // );
      // const diffMs = Math.abs(endDate.getTime() - purchaseDate.getTime()); // milliseconds
      // const diffDays = Math.floor(diffMs / 86400000); // days
      // const diffHrs = Math.floor((diffMs % 86400000) / 3600000); // hours
      // const diffMins = Math.round(((diffMs % 86400000) % 3600000) / 60000); // minutes
      // expiryTime = diffDays ? diffDays + ' days ' + diffHrs + ' hours' : diffHrs + ' hours';
      // return expiryTime;
      // const milisec = +slaDate * 60 * 1000;
      const getTime = this.getDays(slaDate);
      const daysArr = getTime.split(':');
      if (daysArr[0] === '0') {
        expiryTime = daysArr[1] + ' hours ' + daysArr[2] + 'min';
      } else if (daysArr[0] === '-0') {
        expiryTime = '-' + daysArr[1] + ' hours ' + daysArr[2] + 'min';
      } else {
        expiryTime = daysArr[0] + ' days ' + daysArr[1] + ' hours';
      }
      // expiryTime =
      //   daysArr[0] !== '0' && daysArr[0] !== '-0'
      //     ? daysArr[0] + ' days ' + daysArr[1] + ' hours'
      //     : daysArr[1] + ' hours ' + daysArr[2] + 'min';
      return expiryTime;
    } else {
      return expiryTime;
    }
  }

  calculateExpiry(sla: string) {
    let time = '';
    if (sla) {
      const slaDate: any = new Date(sla);
      const currentDate: any = new Date();
      const millisec = slaDate - currentDate;
      time = this.getExpiryTime(millisec);
    }
    return time;
  }

  getDays(ms: number) {
    let isNeagativeVal: boolean;
    if (ms < 0) {
      isNeagativeVal = true;
      ms = Math.abs(ms);
    }
    const days = Math.floor(ms / (24 * 60 * 60 * 1000));
    const daysms = ms % (24 * 60 * 60 * 1000);
    const hours = Math.floor(daysms / (60 * 60 * 1000));
    const hoursms = ms % (60 * 60 * 1000);
    const minutes = Math.floor(hoursms / (60 * 1000));
    const minutesms = ms % (60 * 1000);
    const sec = Math.floor(minutesms / 1000);
    if (isNeagativeVal) {
      return '-' + days + ':' + hours + ':' + minutes + ':' + sec;
    } else {
      return days + ':' + hours + ':' + minutes + ':' + sec;
    }
  }

  getUserSysId(accessingFrom?: string) {
    return new Promise((resolve, reject) => {
      this.authService.getUserSysId().then(
        (userSysId: string) => {
          this.loggedInUserSysId = userSysId;
          resolve(this.loggedInUserSysId);
        },
        err => {
          reject(err);
        }
      );
    });
  }

  getTicketsBasedOnUser(durationRange: any, states?: any) {
    this.authService.getUserSysId().then((userSysID: any) => {
      this.getAssignmentGroupForUser(userSysID).subscribe(assignmentGroups => {
        this.assignmentGroups = assignmentGroups;
      });

      // let userData: any = {};
      // const userId = this.authService.getAuthenticatedUserId();
      // userData = this.dataStore.getData(true, userId);

      this.getDashboardData(durationRange.fromDate, durationRange.toDate, states);
    });
  }

  getTicketsBasedOnUserAssignmentGroups(durationRange?: any, states?: any) {
    this.authService.getUserSysId().then((userSysID: any) => {
      this.getAssignmentGroupForUser(userSysID).subscribe(
        assignmentGroups => {
          let userData: any = {};
          const userId = this.authService.getAuthenticatedUserId();
          userData = this.dataStore.getData(true, userId);

          const fromDate = userData.ticketingFromDate;
          const toDate = userData.ticketingToDate;
          this.assignmentGroups = assignmentGroups;
          this.getDashboardData(durationRange.fromDate, durationRange.toDate, states);
        },
        error => {
          this.assignmentGroups = null;
        }
      );
    });
  }

  getTicketsBasedOnUserautomationApproval(durationRange?: any, states?: any) {
    this.authService.getUserSysId().then((userSysID: any) => {
      this.getAssignmentGroupForUser(userSysID).subscribe(
        assignmentGroups => {
          let userData: any = {};
          const userId = this.authService.getAuthenticatedUserId();
          userData = this.dataStore.getData(true, userId);

          const fromDate = userData.ticketingFromDate;
          const toDate = userData.ticketingToDate;
          this.assignmentGroups = assignmentGroups;
          this.getDashboardData(durationRange.fromDate, durationRange.toDate, states);
        },
        error => {
          this.assignmentGroups = null;
        }
      );
    });
  }

  getTasksBasedOnUser(durationRange?: any, states?: any) {
    this.authService.getUserSysId().then((userSysID: any) => {
      this.getAssignmentGroupForUser(userSysID).subscribe(
        assignmentGroups => {
          let userData: any = {};
          const userId = this.authService.getAuthenticatedUserId();
          userData = this.dataStore.getData(true, userId);

          const fromDate = userData.ticketingFromDate;
          const toDate = userData.ticketingToDate;
          this.assignmentGroups = assignmentGroups;
          this.getDashboardData(durationRange.fromDate, durationRange.toDate, states);
        },
        error => {
          this.assignmentGroups = null;
        }
      );
    });
  }

  getTasksBasedOnAssignmentGroup(durationRange?: any, states?: any) {
    this.authService.getUserSysId().then((userSysID: any) => {
      this.getAssignmentGroupForUser(userSysID).subscribe(
        assignmentGroups => {
          let userData: any = {};
          const userId = this.authService.getAuthenticatedUserId();
          userData = this.dataStore.getData(true, userId);

          const fromDate = userData.ticketingFromDate;
          const toDate = userData.ticketingToDate;
          this.assignmentGroups = assignmentGroups;
          this.getDashboardData(durationRange.fromDate, durationRange.toDate, states);
        },
        error => {
          this.assignmentGroups = null;
        }
      );
    });
  }

  public getAssignmentGroups(): Observable<any> {
    return this.apiService.get(ticketAPIConfig.getAssignmentGroups);
  }

  public getUsersBasedOnAssignmentGroup(user_sys_id: string): Observable<any> {
    return this.apiService.post(ticketAPIConfig.getUsersBasedOnAssignmentGroup, { id: user_sys_id });
  }

  updateTicket(assignPopupData: any) {
    let endPoint = ticketAPIConfig.updateTicket;
    if (this.ticketDataType === 'myTasks' || this.ticketDataType === 'assignmentGroupTasks') {
      endPoint = ticketAPIConfig.updateServiceTask;
    }
    return this.apiService.post(endPoint, assignPopupData);
  }

  updateTicketDataType(optValue: string, durationRange?: any, states?: any) {
    // this.userSysId = userSysId;
    this.resetTicketData();
    this.ticketDataType = optValue;

    if (!durationRange) {
      return;
    }

    this._dateduration$.next(durationRange);

    if (!optValue || optValue === 'myTickets') {
      this.getTicketsBasedOnUser(durationRange, states);
    } else if (optValue === 'assignmentGroupTickets') {
      // assignmentGroupTickets
      this.getTicketsBasedOnUserAssignmentGroups(durationRange, states);
    } else if (optValue === 'myTasks') {
      // get data for my tasks
      this.getTasksBasedOnUser(durationRange, states);
    } else if (optValue === 'assignmentGroupTasks') {
      // get data for assignment group tasks
      this.getTasksBasedOnAssignmentGroup(durationRange, states);
    } else if (optValue === 'myApprovals') {
      this.getTicketsBasedOnUserautomationApproval(durationRange, states);
    }
  }

  getDurationRange(durationValue: any) {
    const toDate = this.dateTimeService.formatDateTimeToCIAPFormat(new Date(), null, true); // this.formatDate(new Date());
    const custDate = this.calculateLastDate(+durationValue - 1); // durationValue = either from preference or session
    const fromDate = this.dateTimeService.formatDateTimeToCIAPFormat(custDate, null, true); // this.formatDate(custDate);
    return {
      fromDate,
      toDate
    };
  }

  getFailCloseCodes() {
    return this.apiService.get(ticketAPIConfig.getFailCloseCodes);
  }

  getSolutionDetails(body: any) {
    return this.apiService.post(ticketAPIConfig.getSolutionDetails, body);
  }

  setStatusConstants(statusConstants: any) {
    this.statusConstants = statusConstants;
  }

  getStatusConstantValue(ciapValue: string, type: CanonicalType) {
    const matchedConstant = this.statusConstants.find(
      (c: any) =>
        (this.getCanType(ciapValue).toLowerCase() === this.getCanType(c.ciap_value).toLowerCase() ||
          this.getCanType(c.itsm_value).toLowerCase() === this.getCanType(ciapValue).toLowerCase()) &&
        (!c.type ||
          (this.getCanType(c.type) && this.getCanType(c.type).toLowerCase() === this.getCanType(type).toLowerCase()))
    );
    if (matchedConstant) {
      return matchedConstant;
    }
    console.log(
      'Cannot find static constant mapping for: ' +
        ciapValue +
        '. Please verify canonical data against internal mapping data.'
    );
    // console.error(
    //   'Cannot find static constant mapping for: ' +
    //     ciapValue +
    //     '. Please verify canonical data against internal mapping data.'
    // );
    return {
      itsm_value: '',
      itsm_code: ''
    };
  }

  getAllStatusConstants(type?: CanonicalType) {
    if (!type) {
      return this.statusConstants;
    }
    return this.statusConstants.filter(i => {
      return i.type === type;
    });
  }

  getStatusList(type?: any) {
    type = this.getCanType(type);

    let statusList = [];
    // const data = this.ticketingConstants.TICKET_STATUS;
    const ticketingConstants = this.getAllStatusConstants(type);
    for (const statusObject of ticketingConstants) {
      if (!statusObject.itsm_value && statusObject.ciap_code === '') {
        statusList.push(statusObject.ciap_value);
      } else if (statusObject.itsm_value) {
        statusList.push(statusObject.itsm_value);
      }
    }
    statusList.splice(2, 0, 'All Open');
    if (statusList && statusList.length > 0) {
      statusList = [...new Set(statusList)]; // Distinct statuses only (P002613-1681)
    }
    return statusList;
  }

  getStatusCodes(type?: any) {
    type = this.getCanType(type);
    let statusCodes = [];

    const ticketingConstants = this.getAllStatusConstants(type);
    for (const statusObject of ticketingConstants) {
      if (!statusObject.itsm_code && statusObject.ciap_code === '') {
        statusCodes.push(statusObject.ciap_value);
      } else if (statusObject.itsm_code) {
        statusCodes.push(statusObject.itsm_code);
      }
    }
    if (statusCodes && statusCodes.length > 0) {
      statusCodes = [...new Set(statusCodes)]; // Distinct statuses only (P002613-1681)
    }
    // console.log('statusCodes 1037', statusCodes);
    return statusCodes;
  }

  resolveTicket(
    selectedKOID: string,
    ticketDetails: any,
    failReason: string,
    closeCode: string,
    workNotes: string,
    closeNotes?: string
  ) {
    let resolvedBy: string;
    const kbArticle = selectedKOID ? selectedKOID : null;

    if (kbArticle) {
      resolvedBy = 'Knowledge Object';
    } else {
      resolvedBy = 'Manual';
    }
    const appConfig = this.runTimeConfigService.getConfig('appConfig');
    const envName = appConfig.environment;
    // todo: Need to update this code once api configs are moved to root level
    return this.apiService.post(ticketAPIConfig.updateTicket, {
      sys_id: ticketDetails.sys_id,
      kb_article: kbArticle,
      created_by: 'CommandTower',
      resolved_by: this.loggedInUserSysId,
      incident_resolved_by: resolvedBy, // KnowledgeObject
      fail_reason: failReason,
      close_code: closeCode,
      work_notes: workNotes,
      close_notes: closeNotes,
      approval_status: ticketDetails.approval_status,
      state: this.getStatusConstantValue('Resolved', 'incident').itsm_code
    });
  }

  closeTask(taskDetails: any, workNotesComments: string, areComments: boolean, state: string) {
    const payload = {
      sys_id: taskDetails.sys_id,
      kb_article: taskDetails.kb_article,
      created_by: 'CommandTower',
      resolved_by: this.loggedInUserSysId,
      task_resolved_by: 'Manual',
      state,
      work_notes: undefined,
      comments: undefined
    };
    if (areComments) {
      payload.comments = workNotesComments;
    } else {
      payload.work_notes = workNotesComments;
    }

    return this.apiService.post(ticketAPIConfig.updateServiceTask, payload);
  }

  downloadAttachment(fileName: string, fileUrl: string) {
    const extension = fileUrl.replace(/^.*\./, '');
    const fileNameNoExt = fileName.replace(/\.[^/.]+$/, '');

    const requestOptions: any = {
      responseType: 'text'
    };

    this.apiService
      .post(
        ticketAPIConfig.getDocument,
        {
          id: fileUrl
        },
        requestOptions
      )
      .subscribe(
        (data: any) => {
          if (data) {
            this.exportService.saveFileBase64(data, fileNameNoExt, extension);
          } else {
            this.toastService.showToast(
              'lang.commonComponents.commonWords.commonToasterMessage.unexpectedFileData',
              'toastError',
              { clearToasts: true }
            );
          }
        },
        error => {
          // this.resetTicketData();
          this.toastService.showToast(
            'lang.commonComponents.commonWords.commonToasterMessage.unexpectedFileData',
            'toastError',
            { clearToasts: true }
          );
        }
      );
  }

  downloadAtttachmentSysID(attachType: any, fileName: string, attachID: string, sysId: any, ticketNumber: any) {
    const extension = fileName.replace(/^.*\./, '');
    const fileNameNoExt = fileName.replace(/\.[^/.]+$/, '');
    this.apiService
      .post(KnowledgeAPIConfig.downloadattachmentEI, {
        sys_id: sysId,
        number: ticketNumber,
        attachment_id: attachID,
        type: attachType
      })
      .subscribe(
        (res: any) => {
          if (res) {
            this.exportService.saveFileBase64(res.base64, fileNameNoExt, extension);
          } else {
            this.toastService.showToast(
              'lang.commonComponents.commonWords.commonToasterMessage.unexpectedFileData',
              'toastError',
              { clearToasts: true }
            );
          }
        },
        error => {
          // this.resetTicketData();
          this.toastService.showToast(
            'lang.commonComponents.commonWords.commonToasterMessage.unexpectedFileData',
            'toastError',
            { clearToasts: true }
          );
        }
      );
  }

  getPendingReasons() {
    // todo getTicketElement?field={field}
    return this.apiService.get(ticketAPIConfig.getTicketElement, {
      params: {
        field: 'u_pending_reason'
      }
    });
  }

  updatePendingState(assignmentRequest: any) {
    if (assignmentRequest.work_notes && !assignmentRequest.work_notes.trim()) {
      delete assignmentRequest.work_notes;
    }
    if (assignmentRequest.assignment_group && !assignmentRequest.assignment_group.trim()) {
      delete assignmentRequest.assignment_group;
    }
    if (assignmentRequest.assigned_to && !assignmentRequest.assigned_to.trim()) {
      delete assignmentRequest.assigned_to;
    }
    const appConfig = this.runTimeConfigService.getConfig('appConfig');
    const envName = appConfig.environment;
    // todo: Need to update this code once api configs are moved to root level

    let endPoint = ticketAPIConfig.updatePendingState;
    if (this.ticketDataType === 'myTasks' || this.ticketDataType === 'assignmentGroupTasks') {
      endPoint = ticketAPIConfig.updateServiceTaskToPendingState;
    }

    return this.apiService.post(endPoint, assignmentRequest);
  }

  uploadPendingStateAttachment(attachmentDetails: any) {
    const appConfig = this.runTimeConfigService.getConfig('appConfig');
    const envName = appConfig.environment;
    // todo: Need to update this code once api configs are moved to root level

    let endPoint = ticketAPIConfig.uploadAttachment;
    if (this.ticketDataType === 'myTasks' || this.ticketDataType === 'assignmentGroupTasks') {
      endPoint = ticketAPIConfig.uploadAttachmentToServiceTask;
    }

    return this.apiService.post(endPoint, attachmentDetails).pipe(
      tap((res: any) => {
        setTimeout(() => {
          this.toastService.showToast(
            [attachmentDetails.filename, 'app.lang.ticketing.toastMessage.pendingFileUploadSuccess'],
            'toastAcknowledgement',
            {
              classname: 'bg-success text-light'
            }
          );
        });
      })
    );
  }
  getSolutionListOfAutomation() {
    return this.apiService.get(ticketAPIConfig.getSolutionListOfAutomation, {}, []);
  }

  updatesolutionworkflowstatus(ticketData: any) {
    if (this.autoTaskSelected$) {
      return this.apiService.post(ticketAPIConfig.updatesctasksolutionworkflowstatus, ticketData);
    } else {
      return this.apiService.post(ticketAPIConfig.updatesolutionworkflowstatus, ticketData);
    }
  }

  getJBPMProcesses(body: any) {
    const headers = new HttpHeaders().set('JBPMCT-Authorization', 'Basic d2JhZG1pbjp3YmFkbWlu');
    return this.apiService.post(
      ticketAPIConfig.getJBPMProcesses,
      body,
      {
        headers,
        params: {
          mapper: 'NodeInstances'
        }
      },
      [{ param: 'queryName', replaceWith: 'jbpmProcessInstanceLogs' }]
    );
  }

  getJBPMProcessImage(processId: any) {
    const headers = new HttpHeaders().set('JBPMCT-Authorization', 'Basic d2JhZG1pbjp3YmFkbWlu');
    const requestOptions: any = {
      headers,
      responseType: 'text',
      params: {
        svgCompletedColor: '#E3E3E3',
        svgCompletedBorderColor: '#5EA4CA',
        svgActiveBorderColor: '#BEBEBE'
      }
    };
    return this.apiService.get(ticketAPIConfig.getJBPMProcessImage, requestOptions, [
      { param: 'containerId', replaceWith: 'CIAP_SapOps_1.0.0-SNAPSHOT' },
      { param: 'processInstanceId', replaceWith: processId }
    ]);
  }

  getRITMAttachmentDetails(sysId: string) {
    return this.apiService.get(ticketAPIConfig.getRitmAttachmentDetails, {}, [{ param: 'sysId', replaceWith: sysId }]);
  }

  getIncidentKODetailsBasedOnNumber(ticketId: string) {
    if (this.ticketDataType === 'myApprovals') {
      if (this.autoTaskSelected$) {
        return this.apiService.get(ticketAPIConfig.getSCTaskDetailsBasedOnNumber, {}, [
          { param: 'number', replaceWith: ticketId }
        ]);
      } else {
        return this.apiService.get(ticketAPIConfig.getIncidentKODetailsBasedOnNumber, {}, [
          { param: 'number', replaceWith: ticketId }
        ]);
      }
    } else if (this.ticketDataType !== 'myTasks' && this.ticketDataType !== 'assignmentGroupTasks') {
      return this.apiService.get(ticketAPIConfig.getIncidentKODetailsBasedOnNumber, {}, [
        { param: 'number', replaceWith: ticketId }
      ]);
    } else {
      return this.apiService.get(ticketAPIConfig.getSCTaskDetailsBasedOnNumber, {}, [
        { param: 'number', replaceWith: ticketId }
      ]);
    }
  }

  getProcessVariable(instanceId: any) {
    const headers = new HttpHeaders().set('JBPMCT-Authorization', 'Basic d2JhZG1pbjp3YmFkbWlu');
    return this.apiService.get(ticketAPIConfig.getProcessVariable, { headers }, [
      { param: 'processInstanceId', replaceWith: instanceId }
    ]);
  }

  getWorkNotesComments(ticketSysId: string) {
    let endPoint;
    if (this.getCanType() === 'incident') {
      endPoint = ticketAPIConfig.getWorkNotesIncident;
    } else if (this.getCanType() === 'task') {
      endPoint = ticketAPIConfig.getWorkNotesTask;
    }

    return this.apiService.get(endPoint, null, [{ param: 'ticketSysId', replaceWith: ticketSysId }]);
  }

  addCommentsWorkNotes(payload: any) {
    let endPoint = ticketAPIConfig.updateTicket;

    if (this.ticketDataType === 'myTasks' || this.ticketDataType === 'assignmentGroupTasks') {
      endPoint = ticketAPIConfig.updateServiceTask;
    }

    return this.apiService.post(endPoint, payload);
  }

  getRequestnumberDetails(sysId: string) {
    return this.apiService.get(ticketAPIConfig.getRequestnumberDetails, {}, [{ param: 'sys_id', replaceWith: sysId }]);
  }

  getRITMDetails(sysId: string) {
    return this.apiService.get(ticketAPIConfig.getRITMDetails, {}, [{ param: 'sys_id', replaceWith: sysId }]);
  }

  matches(country: any, term: string, pipe: PipeTransform) {
    country.incident_type = country.incident_type ? country.incident_type : '';
    country.number = country.number ? country.number : '';
    country.short_description = country.short_description ? country.short_description : '';
    country.priority_value = country.priority_value ? country.priority_value : '';
    country.application = country.application ? country.application : '';
    country.state_value = country.state_value ? country.state_value : '';
    country.solution_type = country.solution_type ? country.solution_type : '';
    country.technology = country.technology ? country.technology : '';
    country.assignment_group_value = country.assignment_group_value ? country.assignment_group_value : '';
    country.resolve_type = country.resolve_type ? country.resolve_type : '';
    country.ko_id = country.ko_id ? country.ko_id : '';
    country.solutionCategory = country.solutionCategory ? country.solutionCategory : '';
    country.solutionSubCategory = country.solutionSubCategory ? country.solutionSubCategory : '';
    country.calculatedExpiry = '';
    if (
      country.state_value !== this.getStatusConstantValue('Resolved', 'incident').itsm_value &&
      country.state_value !== this.getStatusConstantValue('closed', 'incident').itsm_value
    ) {
      country.calculatedExpiry = this.calculateExpiry(country.sla);
    } else {
      country.calculatedExpiry = '';
    }

    return (
      country.number.toLowerCase().includes(term) ||
      pipe.transform(country.incident_type.toLowerCase()).includes(term) ||
      pipe.transform(country.short_description.toLowerCase()).includes(term) ||
      pipe.transform(country.ko_id.toLowerCase()).includes(term) ||
      pipe.transform(country.priority_value.toLowerCase()).includes(term) ||
      pipe.transform(country.application.toLowerCase()).includes(term) ||
      pipe.transform(country.state_value.toLowerCase()).includes(term) ||
      pipe.transform(country.solution_type.toLowerCase()).includes(term) ||
      pipe.transform(country.technology.toLowerCase()).includes(term) ||
      pipe.transform(country.assignment_group_value.toLowerCase()).includes(term) ||
      pipe.transform(country.resolve_type.toLowerCase()).includes(term) ||
      pipe.transform(country.calculatedExpiry.toLowerCase()).includes(term) ||
      pipe.transform(country.solutionCategory.toLowerCase()).includes(term) ||
      pipe.transform(country.solutionSubCategory.toLowerCase()).includes(term)
    );
  }
  validateKO(koId: string, ticketId?: string) {
    return this.apiService.get(ticketAPIConfig.validateKO, {
      params: {
        ticketId,
        koId
      }
    });
  }
  showRelatedKOs(ticketId?: string) {
    return this.apiService.get(ticketAPIConfig.showRelatedKOs, { params: { ticketId } });
  }
  // Since ticketDataType does not store type as incident or task but rather as myTickets/assignmentGroupTickets or myTasks/assignmentGroupTasks, return the equivalent value
  getCanType(type?: any) {
    type = type || this.ticketDataType;

    if (!type) {
      return 'incident';
    }

    if (type && (type === 'myTickets' || type === 'assignmentGroupTickets')) {
      return 'incident';
    } else if ((type && type === 'myTasks') || type === 'assignmentGroupTasks') {
      return 'task';
      // commenting as status color was not displaying properly
      // } else if ((type && type === 'myTasks') || type === 'automationApproval') {
      //   return 'task';
    } else if (type === 'myApprovals') {
      if (this.autoTaskSelected$) {
        return 'task';
      } else {
        return 'incident';
      }
    }
    return type;
  }

  /*
   * At run time CIAP/ITSM status will be checked against canonical
   * checkValidationStatus('Resolved',<dynamicValuefromAPI>) <-- this will be fetched from Canonical API
   * checkValidationStatus('Resolved',<dynamicValuefromAPI>) <-- this will be actual value in ITSM
   * Returns true if match is found
   *
   */
  checkValidationStatus(validationName: string, valueToCheckAgainst: string) {
    let checkBy: 'CIAPStatus' | 'ITSMStatus';

    const validations = this.ticketingSettings.validations;
    const validationValue = validations.find(
      vN => vN.validationName.toLowerCase() === validationName.toLowerCase() && vN.forType === this.getCanType()
    );

    if (validationValue.disableForITSMStatus) {
      checkBy = 'ITSMStatus';
    } else {
      checkBy = 'CIAPStatus';
    }

    if (!validationValue) {
      console.error('No validation with name ' + validationName + ' found in ticketing settings.');
    }

    let matchSuccessful;
    let listOfCanonicalStatuses;
    let disableForITSMStatus;
    if (checkBy === 'ITSMStatus') {
      disableForITSMStatus = validationValue.disableForITSMStatus;
      listOfCanonicalStatuses = this.getStatusList(this.ticketDataType);
    } else if (checkBy === 'CIAPStatus') {
      const disableForCIAPStatus = validationValue.disableForCIAPStatus;
      listOfCanonicalStatuses = this.getStatusList(this.ticketDataType);
      disableForITSMStatus = [];

      for (const st of disableForCIAPStatus) {
        disableForITSMStatus.push(this.getStatusConstantValue(st, this.ticketDataType).itsm_value);
      }
    }
    if (
      listOfCanonicalStatuses &&
      disableForITSMStatus &&
      listOfCanonicalStatuses.some(r => disableForITSMStatus.includes(r))
    ) {
      matchSuccessful =
        disableForITSMStatus.indexOf(this.getStatusConstantValue(valueToCheckAgainst, this.ticketDataType).itsm_value) >
        -1;
    }
    return matchSuccessful;
  }
  checkUserOptionValue() {
    return this.ticketDataType;
  }

  getStatusConstantName(itsmCode: string, type: CanonicalType) {
    const matchedConstant = this.statusConstants.find(
      (c: any) =>
        this.getCanType(c.itsm_code).toLowerCase() === this.getCanType(itsmCode).toLowerCase() &&
        (!c.type ||
          (this.getCanType(c.type) && this.getCanType(c.type).toLowerCase() === this.getCanType(type).toLowerCase()))
    );
    if (matchedConstant) {
      return matchedConstant;
    }
    console.error(
      'Cannot find static constant mapping for: ' +
        itsmCode +
        '. Please verify canonical data against internal mapping data.'
    );
    return {
      itsm_value: '',
      itsm_code: ''
    };
  }
  getDynamicMenuasPerTenant(configName: any) {
    //
    console.log('service called getDynamicMenuasPerTenantx ');
    return this.apiService.get(ticketAPIConfig.getDynamicMenuAsPerTenant, {}, [
      { param: 'configName', replaceWith: configName }
    ]);
  }
  private _set(patch: Partial<State>) {
    Object.assign(this._state, patch);
    this._search$.next();
  }

  private _search(): Observable<SearchResult> {
    const { sortColumn, sortDirection, pageSize, page, searchTerm } = this._state;
    this.dashboardData.forEach(dashboardRow => {
      if (
        dashboardRow.state_value &&
        dashboardRow.state_value.toLowerCase() ===
          this.getStatusConstantValue('Resolved', 'incident').itsm_value.toLowerCase()
      ) {
        dashboardRow.sla = 0;
      }
    });

    // 1. sort
    let countries = sort(this.dashboardData, sortColumn, sortDirection);
    const totalSortedData = sort(this.dashboardData, sortColumn, sortDirection);
    this.dashboardData = totalSortedData;
    // 2. filter
    countries = countries.filter(country => this.matches(country, searchTerm, this.pipe));
    const total = countries.length;

    // 3. paginate
    countries = pageSize ? countries.slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize) : countries;
    return of({ countries, total, totalSortedData });
  }

  linkKO(uid: string, ticketId?: string) {
    return this.apiService.post(ticketAPIConfig.userFeedBack, {
      solutionnumber: uid,
      incidentnumber: ticketId
      //incident_type: this.ticketDataType === 'myTasks' || this.ticketDataType === 'assignmentGroupTasks' ? 'task' : 'incident'
    });
  }

  updateTicketLinkKO(kb_sys_id: string, kb_article: string, knowledgeObjects: [], toggle?: any) {
    const endPoint = toggle === 'ServiceRequests' ? ticketAPIConfig.updateServiceTask : ticketAPIConfig.updateTicket;

    // todo: Need to update this code once api configs are moved to root level
    return this.apiService.post(endPoint, {
      sys_id: kb_sys_id,
      kb_article: kb_article,
      created_by: 'CommandTower',
      knowledgeObjects: knowledgeObjects
    });
  }
}
